import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5mj873lpot0rgnb7prg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-5">Ready for Professional Bulk Laundry Care?</h2>
          <p className="text-xl text-gray-600 mb-4">Schedule your first bulk pickup today and experience the difference</p>
          <div className="flex items-center justify-center gap-2 text-amber-600">
            <i className="ri-phone-line text-2xl"></i>
            <a href="tel:+918850056363" className="text-2xl font-semibold hover:text-amber-700 transition-colors">
              +91 88500 56363
            </a>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} data-readdy-form className="bg-stone-50 rounded-3xl p-8 md:p-12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Dry Cleaning">Dry Cleaning</option>
                  <option value="Wash & Fold">Wash &amp; Fold</option>
                  <option value="Ironing & Pressing">Ironing &amp; Pressing</option>
                  <option value="Alterations & Repairs">Alterations &amp; Repairs</option>
                  <option value="Pickup & Delivery">Pickup &amp; Delivery</option>
                  <option value="Subscription Package">Subscription Package</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                maxLength={500}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all resize-none text-sm"
                placeholder="Tell us about your laundry needs..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-600 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-amber-700 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Sending...' : 'Schedule Pickup Now'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-800 text-sm font-medium text-center">
                  Thank you! We will contact you shortly to confirm your pickup.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-800 text-sm font-medium text-center">
                  Something went wrong. Please try again or call us directly.
                </p>
              </div>
            )}
          </form>
        </div>

        <div className="mt-20 flex items-center justify-center gap-12 flex-wrap">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
              <i className="ri-washing-machine-line text-amber-600 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-600">Professional Equipment</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
              <i className="ri-iron-line text-amber-600 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-600">Expert Pressing</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
              <i className="ri-t-shirt-line text-amber-600 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-600">Quality Folding</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
              <i className="ri-truck-line text-amber-600 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-600">Fast Delivery</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
              <i className="ri-handbag-line text-amber-600 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-600">Careful Packaging</p>
          </div>
        </div>
      </div>
    </section>
  );
}
